import React from "react";
import { useAuth } from "@/context/AuthContext";
import { Mail, Phone, ShieldCheck, MapPin, Copy, IdCard } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, provider } = useAuth();
  if (!user) return null;

  const copyCode = () => {
    if (!user.code) return;
    navigator.clipboard.writeText(user.code).then(() => toast.success("ID copied"));
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6" data-testid="account-page">
      <h1 className="font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">Account</h1>

      {user.code && (
        <button
          onClick={copyCode}
          className="w-full overflow-hidden rounded-3xl border border-brand-orange/30 bg-gradient-to-br from-brand-ink to-[#1a2340] p-6 text-left text-white transition-transform hover:-translate-y-0.5"
          data-testid="account-id-card"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-white/70">
              Your TridentDigitalPulse ID
            </div>
            <Copy size={14} className="text-white/70" />
          </div>
          <div className="mt-2 font-mono text-2xl font-bold tracking-widest sm:text-3xl" data-testid="account-id">
            {user.code}
          </div>
          <div className="mt-3 flex items-center gap-3 text-[11px] text-white/60">
            <span className="rounded-full bg-white/10 px-2 py-0.5">
              State: {user.code.slice(0, 2)}
            </span>
            <span className="rounded-full bg-white/10 px-2 py-0.5 uppercase">
              {user.role}
            </span>
          </div>
        </button>
      )}

      <div className="rounded-3xl border border-foreground/10 bg-white p-6">
        <div className="flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-ink font-heading text-2xl font-bold text-white">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-heading text-xl font-bold">{user.name}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">{user.role}</div>
          </div>
        </div>

        <div className="mt-6 space-y-3 text-sm">
          <div className="flex items-center gap-3 rounded-xl border p-3">
            <Mail size={16} className="text-muted-foreground" /> {user.email}
          </div>
          {user.phone && (
            <div className="flex items-center gap-3 rounded-xl border p-3">
              <Phone size={16} className="text-muted-foreground" /> {user.phone}
            </div>
          )}
          {user.state && (
            <div className="flex items-center gap-3 rounded-xl border p-3">
              <MapPin size={16} className="text-muted-foreground" /> {user.state}
            </div>
          )}
          {user.aadhaar_last4 && (
            <div className="flex items-center gap-3 rounded-xl border p-3">
              <IdCard size={16} className="text-muted-foreground" /> Aadhaar ••••••••{user.aadhaar_last4}
            </div>
          )}
        </div>
      </div>

      {provider && (
        <div className="rounded-3xl border border-foreground/10 bg-white p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-brand-orange" />
            <div className="font-heading text-base font-bold">Provider status</div>
          </div>
          <div className="mt-2 inline-flex rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-widest">
            KYC · {provider.kyc?.status}
          </div>
          {provider.bio && <p className="mt-3 text-sm text-foreground/80">{provider.bio}</p>}
        </div>
      )}
    </div>
  );
}
